import { useState } from 'react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import type Employee from 'modules/employees/store/models/Employee'
import type { PolicyPeriodAuthorizations } from 'modules/payroll_bundle/lib/types'
import type PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

const defaultPolicyPeriodAuthorizations = {
  edit_observations: false,
  edit_supplements: false,
  validate_supplements: false,
}

interface SupplementRecord {
  canCreateEmployeeSupplementsValidation: boolean
  canReadEmployeeSupplementsValidation: boolean
  canEditEmployeeSupplements: boolean
  canEditEmployeeObservation: boolean
}

interface Props {
  policyPeriod?: PolicyPeriod | null
  employees: Employee[]
  policyPeriodStatus?: string
}

const useBuildPermissions = ({ policyPeriod, policyPeriodStatus, employees }: Props) => {
  const SUPPLEMENT_VALIDATION_CREATE_PERMISSION_KEY = 'supplements_validations.create'
  const SUPPLEMENTS_VALIDATION_READ_PERMISSION_KEY = 'supplements_validations.read'
  const SUPPLEMENTS_CREATE_PERMISSION_KEY = 'payroll_supplements.supplements.create'
  const SUPPLEMENTS_OBSERVATION_CREATE_PERMISSION_KEY = 'payroll_supplements.observations.create'
  const [policyPeriodAuthorizations, setPolicyPeriodAuthorizations] =
    useState<PolicyPeriodAuthorizations>(defaultPolicyPeriodAuthorizations)

  const { loading: loadingPolicyPeriodAuthorizations } = useAsyncData(async () => {
    if (!policyPeriod) return

    const result = await policyPeriod.statusAuthorizations()

    setPolicyPeriodAuthorizations(result)
  }, [policyPeriodStatus])

  const { loading: loadingValidationAuthorizations } = useAsyncData(async () => {
    await Promise.all([
      policyAuthorizations.authorizePolicies(
        SUPPLEMENT_VALIDATION_CREATE_PERMISSION_KEY,
        employees
      ),
      policyAuthorizations.authorizePolicies(SUPPLEMENTS_VALIDATION_READ_PERMISSION_KEY, employees),
      policyAuthorizations.authorizePolicies(SUPPLEMENTS_CREATE_PERMISSION_KEY, employees),
      policyAuthorizations.authorizePolicies(
        SUPPLEMENTS_OBSERVATION_CREATE_PERMISSION_KEY,
        employees
      ),
    ])
  }, [employees.length])

  return useUnsafeMemo(() => {
    const defaultSupplementsPermissions = () => {
      const supplementPermissions: Record<number, SupplementRecord> = {}
      employees.forEach((employee) => {
        supplementPermissions[employee.id] = {
          canCreateEmployeeSupplementsValidation: policyAuthorizations.for(
            SUPPLEMENT_VALIDATION_CREATE_PERMISSION_KEY,
            employee
          ),
          canReadEmployeeSupplementsValidation: policyAuthorizations.for(
            SUPPLEMENTS_VALIDATION_READ_PERMISSION_KEY,
            employee
          ),
          canEditEmployeeSupplements: policyAuthorizations.for(
            SUPPLEMENTS_CREATE_PERMISSION_KEY,
            employee
          ),
          canEditEmployeeObservation: policyAuthorizations.for(
            SUPPLEMENTS_OBSERVATION_CREATE_PERMISSION_KEY,
            employee
          ),
        }
      })

      const loading = loadingValidationAuthorizations

      return {
        loading,
        policyPeriodAuthorizations,
        supplementPermissions,
      }
    }

    if (!policyPeriodStatus) {
      return defaultSupplementsPermissions()
    }

    const supplementPermissions: Record<number, SupplementRecord> = {}

    const loading = loadingPolicyPeriodAuthorizations || loadingValidationAuthorizations

    if (loading) {
      return {
        loading,
        policyPeriodAuthorizations,
        supplementPermissions,
      }
    }

    const canCreateEmployeeSupplementsValidation = (employee: Employee) =>
      !!policyPeriodAuthorizations.validate_supplements &&
      policyAuthorizations.for(SUPPLEMENT_VALIDATION_CREATE_PERMISSION_KEY, employee)

    const canReadEmployeeSupplementsValidation = (employee: Employee) =>
      !!policyPeriodAuthorizations.validate_supplements &&
      policyAuthorizations.for(SUPPLEMENTS_VALIDATION_READ_PERMISSION_KEY, employee)

    const canEditEmployeeSupplements = (employee: Employee) =>
      !!policyPeriodAuthorizations.edit_supplements &&
      policyAuthorizations.for(SUPPLEMENTS_CREATE_PERMISSION_KEY, employee)

    const canEditEmployeeObservation = (employee: Employee) =>
      !!policyPeriodAuthorizations.edit_observations &&
      policyAuthorizations.for(SUPPLEMENTS_OBSERVATION_CREATE_PERMISSION_KEY, employee)

    employees.forEach((employee) => {
      supplementPermissions[employee.id] = {
        canCreateEmployeeSupplementsValidation: canCreateEmployeeSupplementsValidation(employee),
        canReadEmployeeSupplementsValidation: canReadEmployeeSupplementsValidation(employee),
        canEditEmployeeSupplements: canEditEmployeeSupplements(employee),
        canEditEmployeeObservation: canEditEmployeeObservation(employee),
      }
    })

    return {
      loading,
      policyPeriodAuthorizations,
      supplementPermissions,
    }
  }, [
    policyPeriodStatus,
    loadingPolicyPeriodAuthorizations,
    loadingValidationAuthorizations,
    policyPeriodAuthorizations,
    employees,
  ])
}

export default useBuildPermissions
